import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FileUploader from './FileUploader';
import ErrorMessage from '../common/ErrorMessage';
import { PLANS } from '../../config/plans';
import './AnonymousDocumentManager.css';

interface AnonymousDocument {
  documentId: string;
  name: string;
  fileSize: number;
  pageCount: number;
  createdAt: string;
  expiresAt: string;
  shareableLink: string;
}

interface AnonymousDocumentManagerProps {
  documentId?: string;
  showUploader?: boolean;
  showExpirationWarning?: boolean;
  showRegistrationPrompts?: boolean;
  className?: string;
}

const AnonymousDocumentManager: React.FC<AnonymousDocumentManagerProps> = ({
  showUploader = true,
  showExpirationWarning = true,
  className = ''
}) => {
  const [document, setDocument] = useState<AnonymousDocument | null>(null);
  const [error, setError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const navigate = useNavigate();

  const handleUploadSuccess = (uploadedDocument: any) => {
    const docId = uploadedDocument.document_id || uploadedDocument.documentId;
    setDocument({
      documentId: docId,
      name: uploadedDocument.name || 'Untitled Document',
      fileSize: uploadedDocument.file_size || uploadedDocument.fileSize || 0,
      pageCount: uploadedDocument.page_count || uploadedDocument.pageCount || 0,
      createdAt: uploadedDocument.created_at || uploadedDocument.createdAt || new Date().toISOString(),
      expiresAt: uploadedDocument.expires_at || uploadedDocument.expiresAt || '',
      shareableLink: `${window.location.origin}/view/${docId}`
    });
    setUploadSuccess(true);
  };

  const copyLink = async () => {
    if (!document?.shareableLink) return;
    try {
      await navigator.clipboard.writeText(document.shareableLink);
    } catch {
      const ta = window.document.createElement('textarea');
      ta.value = document.shareableLink;
      window.document.body.appendChild(ta);
      ta.select();
      window.document.execCommand('copy');
      window.document.body.removeChild(ta);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className={`anonymous-document-manager ${className}`}>
      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}

      {uploadSuccess && document ? (
        <div className="upload-success-section">
          <div className="success-header">
            <div className="success-icon">✓</div>
            <div>
              <h5 className="success-title">Ready to share</h5>
              <p className="success-name">{document.name}</p>
            </div>
          </div>

          <div className="link-input-group">
            <input type="text" value={document.shareableLink} readOnly className="form-control" />
            <button
              className={`btn ${linkCopied ? 'btn-success' : 'btn-secondary'}`}
              onClick={copyLink}
            >
              {linkCopied ? '✓ Copied' : 'Copy link'}
            </button>
          </div>

          <div className="success-actions">
            <button className="btn btn-primary" onClick={() => navigate(`/view/${document.documentId}`)}>
              View document
            </button>
            <button className="btn btn-outline-secondary" onClick={() => { setDocument(null); setUploadSuccess(false); }}>
              Upload another
            </button>
          </div>

          <p className="expiry-note">
            Expires in 7 days · <Link to="/register">Create an account</Link> to keep it permanently
          </p>
        </div>
      ) : (
        showUploader && (
          <div className="uploader-section">
            <FileUploader
              maxSize={PLANS.free.maxAnonymousFileSizeMb * 1024 * 1024}
              onUploadSuccess={handleUploadSuccess}
              onUploadError={setError}
              showProgress={true}
            />
            <p className="anon-note">
              {PLANS.free.maxAnonymousFileSizeMb} MB limit · expires in 7 days · <Link to="/register">sign up</Link> for {PLANS.free.maxAuthFileSizeMb} MB
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default AnonymousDocumentManager;
